import { useEffect, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { Navigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const defaultConfig = {
  aws_access_key_id: '',
  aws_secret_access_key: '',
  aws_region: 'us-east-1',
  from_email: 'noreply@dealider.com',
  from_name: APP_NAME,
  company_address: 'Dealider Inc., CDMX, México',
};

export default function EmailConfig() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/backoffice/email-config');
      setStatus(data);
      setConfig(prev => ({
        ...prev,
        aws_access_key_id: data.aws_access_key_id || '',
        aws_secret_access_key: '',
        aws_region: data.aws_region || 'us-east-1',
        from_email: data.from_email || prev.from_email,
        from_name: data.from_name || prev.from_name,
        company_address: data.company_address || prev.company_address,
      }));
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.detail || 'No se pudo cargar la configuración SES');
      }
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/backoffice/email-config', config);
      toast.success('Configuración SES guardada');
      setConfig(prev => ({ ...prev, aws_secret_access_key: '' }));
      await loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/backoffice/email-config/test');
      setTestResult({ success: true, ...data });
      toast.success('Conexión SES validada');
      await loadConfig();
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Error al probar conexión SES' });
      toast.error(error.response?.data?.detail || 'Error al probar conexión SES');
    } finally {
      setTesting(false);
    }
  };

  if (user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="email-config-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="email-config-page-title">Email transaccional y marketing (Amazon SES)</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="email-config-page-subtitle">Configura credenciales AWS sin tocar el servidor. Solo disponible para superadmin.</p>
        </div>
        <Badge className="bg-[#A5D62C]/10 text-[#A5D62C] border border-[#A5D62C]/30" data-testid="email-config-superadmin-badge">
          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> BackOffice Seguro
        </Badge>
      </div>

      {loading ? (
        <Card className="bg-card/50 border-white/10" data-testid="email-config-loading-card">
          <CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando configuración de email...
          </CardContent>
        </Card>
      ) : (
        <>
          <Alert className="border-white/10 bg-card/40" data-testid="email-config-status-alert">
            <Mail className="h-4 w-4" />
            <AlertDescription className="w-full">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div data-testid="email-config-current-status">
                  <p className="font-semibold text-white">
                    Estado:{' '}
                    {status?.last_test_result === 'success' ? '✅ Conectado' : status ? '⚠️ Configurado sin prueba exitosa' : 'ℹ️ Sin configuración guardada'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="email-config-mode-text">
                    Modo: {status?.sandbox_mode ? 'Sandbox (solo correos verificados)' : 'Producción'}
                  </p>
                  {status?.last_test_at && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="email-config-last-test-at">
                      Última prueba: {new Date(status.last_test_at).toLocaleString('es-MX')}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing}
                  data-testid="email-config-test-connection-button"
                >
                  {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Probar conexión
                </Button>
              </div>
            </AlertDescription>
          </Alert>

          {testResult && (
            <Alert
              className={testResult.success ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-red-500/40 bg-red-500/10'}
              data-testid="email-config-test-result-alert"
            >
              {testResult.success ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertCircle className="h-4 w-4 text-red-400" />}
              <AlertDescription>
                <p className="font-semibold" data-testid="email-config-test-result-message">{testResult.message}</p>
                {testResult.quota && (
                  <div className="mt-2 text-xs space-y-1" data-testid="email-config-test-result-quota">
                    <p>Límite 24h: {testResult.quota.max_24_hour_send}</p>
                    <p>Enviados 24h: {testResult.quota.sent_last_24_hours}</p>
                    <p>Tasa máxima: {testResult.quota.max_send_rate} email/seg</p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Card className="bg-card/50 border-white/10" data-testid="email-config-form-card">
            <CardHeader>
              <CardTitle className="text-white" data-testid="email-config-form-title">Credenciales AWS SES</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4" data-testid="email-config-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="aws_access_key_id">AWS Access Key ID</Label>
                    <Input
                      id="aws_access_key_id"
                      value={config.aws_access_key_id}
                      onChange={(e) => setConfig(prev => ({ ...prev, aws_access_key_id: e.target.value }))}
                      placeholder="AKIA..."
                      required
                      data-testid="email-config-access-key-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aws_secret_access_key">AWS Secret Access Key</Label>
                    <Input
                      id="aws_secret_access_key"
                      type="password"
                      value={config.aws_secret_access_key}
                      onChange={(e) => setConfig(prev => ({ ...prev, aws_secret_access_key: e.target.value }))}
                      placeholder="••••••••••"
                      required={!status}
                      data-testid="email-config-secret-key-input"
                    />
                    {status && <p className="text-xs text-muted-foreground" data-testid="email-config-secret-key-hint">Déjalo vacío para conservar la credencial actual.</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>AWS Region</Label>
                    <Select
                      value={config.aws_region}
                      onValueChange={(value) => setConfig(prev => ({ ...prev, aws_region: value }))}
                    >
                      <SelectTrigger data-testid="email-config-region-select">
                        <SelectValue placeholder="Selecciona región" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                        <SelectItem value="us-east-2">US East (Ohio)</SelectItem>
                        <SelectItem value="us-west-1">US West (N. California)</SelectItem>
                        <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                        <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="from_email">Email remitente</Label>
                    <Input
                      id="from_email"
                      type="email"
                      value={config.from_email}
                      onChange={(e) => setConfig(prev => ({ ...prev, from_email: e.target.value }))}
                      required
                      data-testid="email-config-from-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from_name">Nombre remitente</Label>
                    <Input
                      id="from_name"
                      value={config.from_name}
                      onChange={(e) => setConfig(prev => ({ ...prev, from_name: e.target.value }))}
                      required
                      data-testid="email-config-from-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_address">Dirección de empresa</Label>
                    <Input
                      id="company_address"
                      value={config.company_address}
                      onChange={(e) => setConfig(prev => ({ ...prev, company_address: e.target.value }))}
                      required
                      data-testid="email-config-company-address-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2 flex-wrap">
                  <Button type="submit" disabled={saving} data-testid="email-config-save-button">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar configuración
                  </Button>
                  <Button type="button" variant="outline" onClick={loadConfig} data-testid="email-config-reload-button">Recargar</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Alert className="border-white/10 bg-card/40" data-testid="email-config-help-alert">
            <AlertDescription>
              <p className="font-semibold mb-2">Guía rápida SES</p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1" data-testid="email-config-help-list">
                <li>Crear usuario IAM con permisos de SES.</li>
                <li>Configurar Access Key + Secret Key del usuario IAM.</li>
                <li>Verificar dominio y DKIM en SES.</li>
                <li>Si estás en sandbox, solo podrás enviar a correos verificados.</li>
                <li>Usa “Probar conexión” para detectar estado sandbox/producción.</li>
              </ol>
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}
